import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { CaptureClip } from './LivingSculpt.js';
import {
  renderMotionClip,
  downloadClip,
  shareClip,
  canShareClip,
  isTouchDevice,
} from '../lib/motionClip.js';
import type { SaveOutcome } from '../lib/motionClip.js';
import { api } from '../api/client.js';
import { GoldLeafMark } from './CreditBadge.js';
import { cn } from '../lib/classnames.js';

/**
 * MotionClipSheet — the "animate your gilding" studio. A premium sheet that turns
 * the living-sculpt into a downloadable orbit clip, rendered entirely client-side.
 * A free, watermarked clip lets anyone feel the wow; the HD, watermark-free export
 * is one leaf. The render reuses the exact on-screen scene, so the file looks just
 * like the piece — just bigger and on a perfect, looping path.
 */
export function MotionClipSheet({
  open,
  onClose,
  capture,
  frameAspect,
  slug,
  generationId,
  imageIndex = 0,
  onChargeHd,
}: {
  open: boolean;
  onClose: () => void;
  capture: CaptureClip | null;
  frameAspect: number;
  /** Short id used in the download filename. */
  slug: string;
  /** Full generation id — used to read/refresh the HD entitlement. */
  generationId: string;
  /** Which image (variant) the clip renders from. HD is unlocked per image. */
  imageIndex?: number;
  /** Deduct the leaf for HD server-side. Resolves ok=false (with reason) on failure. */
  onChargeHd: () => Promise<{ ok: boolean; error?: string }>;
}) {
  const [phase, setPhase] = useState<'idle' | 'rendering' | 'done'>('idle');
  const [progress, setProgress] = useState(0);
  const [tier, setTier] = useState<'free' | 'hd'>('hd');
  // Video (mp4) for posting; GIF for profile pictures (OpenSea/X/Discord avatars).
  const [format, setFormat] = useState<'video' | 'gif'>('video');
  const [error, setError] = useState<string | null>(null);
  const [lowLeaves, setLowLeaves] = useState(false);
  // Finished clip held for an explicit Save tap. Auto-downloading right after the
  // multi-second render fails: detached from the original gesture, the browser
  // opens the file in a new tab instead of saving it. A real click downloads.
  const [result, setResult] = useState<{
    blob: Blob;
    filename: string;
    hd: boolean;
    // Video was requested but this browser has no in-browser MP4 encoder, so it
    // fell back to a GIF (an image). Surfaced so the user isn't confused why
    // their "video" saved as an image.
    gifFallback: boolean;
  } | null>(null);
  const [saved, setSaved] = useState(false);
  // When the browser can't save programmatically (iOS in-app browsers), we show
  // the clip inline for a press-and-hold / open-in-Safari save. previewUrl is an
  // object URL for that inline <video>/<img>; revoked on close.
  const [manualSave, setManualSave] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && phase !== 'rendering') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, phase]);

  // Reset transient state whenever the sheet (re)opens.
  useEffect(() => {
    if (open) {
      setPhase('idle');
      setProgress(0);
      setError(null);
      setLowLeaves(false);
      setResult(null);
      setSaved(false);
      setManualSave(false);
      setPreviewUrl(null);
    }
  }, [open]);

  // Revoke the inline-preview object URL when it changes or on unmount.
  useEffect(() => {
    if (!previewUrl) return undefined;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  // HD is unlocked once per image. If this image is already paid, the sheet shows
  // "Included" and a re-download (video OR gif) charges nothing.
  const entQ = useQuery({
    queryKey: ['clip-ent', generationId],
    queryFn: () => api.clipEntitlement(generationId),
    enabled: open && !!generationId,
    staleTime: 15_000,
  });
  const alreadyUnlocked = entQ.data?.unlocked?.includes(imageIndex) ?? false;

  if (!open) return null;

  const run = async (hd: boolean): Promise<void> => {
    if (!capture || phase === 'rendering') return;
    setError(null);
    setLowLeaves(false);
    setTier(hd ? 'hd' : 'free');

    if (hd) {
      // Charge the leaf first; only render the clean export once it's paid.
      const charge = await onChargeHd();
      if (!charge.ok) {
        if (charge.error === 'insufficient_credits') setLowLeaves(true);
        else setError('Could not start the HD export. Please try again.');
        return;
      }
    }

    setPhase('rendering');
    setProgress(0);
    setSaved(false);
    try {
      const { blob, ext } = await renderMotionClip({
        capture,
        frameAspect,
        hd,
        format,
        onProgress: (p) => setProgress(p),
      });
      setResult({
        blob,
        filename: `gilding-${slug}${hd ? '-hd' : ''}.${ext}`,
        hd,
        gifFallback: format === 'video' && ext === 'gif',
      });
      // Reflect the new unlock so a follow-up format download shows "Included".
      if (hd) void entQ.refetch();
      setPhase('done');
    } catch (e) {
      console.error('motion clip render failed', e);
      setError('Rendering hit a snag. Please try again.');
      setPhase('idle');
    }
  };

  // Download = keep the FILE (Android → Downloads, desktop → save dialog). On
  // iOS a blob download plays inline, so downloadClip returns 'manual' and we
  // reveal the clip for a press-and-hold / open-in-Safari save.
  const download = (): void => {
    if (!result) return;
    setError(null);
    void downloadClip(result.blob, result.filename)
      .then((outcome: SaveOutcome) => {
        if (outcome === 'manual') {
          setPreviewUrl((prev) => prev ?? URL.createObjectURL(result.blob));
          setManualSave(true);
        } else if (outcome === 'saved' || outcome === 'downloaded') {
          setSaved(true);
        }
        // 'aborted' → user cancelled; claim nothing.
      })
      .catch(() => {
        setError('Saving hit a snag. Try again, or open this page in Safari/Chrome to download.');
      });
  };

  // Share = the native share sheet (post to X, Messages, or Save Video on iOS).
  const share = (): void => {
    if (!result) return;
    setError(null);
    void shareClip(result.blob, result.filename)
      .then((outcome) => {
        if (outcome === 'shared') setSaved(true);
        else if (outcome === 'unsupported') download(); // fall back to a real download
        // 'aborted' → user dismissed; claim nothing.
      })
      .catch(() => download());
  };

  const rendering = phase === 'rendering';
  const pct = Math.round(progress * 100);

  return createPortal(
    <div
      className="backdrop-enter fixed inset-0 z-[60] flex items-end justify-center overflow-y-auto overscroll-contain sm:items-center"
      style={{ background: 'rgb(6 5 8 / 0.8)', backdropFilter: 'blur(8px)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Animate your gilding"
      onClick={() => (rendering ? undefined : onClose())}
    >
      <div
        className="sheet-enter relative m-3 w-full max-w-md overflow-hidden rounded-3xl border border-hairline-strong/70 bg-surface-elevated/95 shadow-lift backdrop-blur-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* gilded top edge */}
        <div aria-hidden className="h-px w-full bg-gradient-to-r from-transparent via-accent/70 to-transparent" />

        <div className="flex flex-col gap-5 p-6">
          <header className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center text-accent-deep">
              <AnimateDownloadGlyph />
            </span>
            <div className="flex flex-col gap-1">
              <h2 className="font-display text-h-meta font-semibold leading-tight text-ink">
                Animate your gilding
              </h2>
              <p className="font-display text-caption italic leading-snug text-ink-subtle">
                A living clip that orbits to reveal the depth, made for your timeline.
              </p>
            </div>
            {!rendering && (
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="ml-auto -mr-1.5 -mt-1.5 inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-subtle transition-colors hover:bg-surface-sunken hover:text-ink"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                  <path d="M5 5l10 10M15 5L5 15" />
                </svg>
              </button>
            )}
          </header>

          {rendering ? (
            <RenderingPanel pct={pct} tier={tier} />
          ) : phase === 'done' && result ? (
            <DonePanel
              hd={result.hd}
              saved={saved}
              manualSave={manualSave}
              previewUrl={previewUrl}
              isGif={result.filename.endsWith('.gif')}
              gifFallback={result.gifFallback}
              onDownload={download}
              onShare={share}
              onAgain={() => setPhase('idle')}
            />
          ) : (
            <div className="flex flex-col gap-3">
              {/* Format — Video for posting, GIF for profile pictures. */}
              <div className="flex items-center justify-between gap-3">
                <span className="font-body text-micro uppercase tracking-wide text-ink-subtle">
                  Format
                </span>
                <div className="inline-flex rounded-full border border-hairline-strong/60 bg-surface-sunken/40 p-0.5">
                  {(['video', 'gif'] as const).map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFormat(f)}
                      className={cn(
                        'rounded-full px-3.5 py-1 font-body text-micro font-semibold transition-colors',
                        format === f ? 'bg-accent/20 text-accent-deep' : 'text-ink-subtle hover:text-ink',
                      )}
                    >
                      {f === 'video' ? 'Video' : 'GIF'}
                    </button>
                  ))}
                </div>
              </div>
              <p className="-mt-1.5 font-body text-micro text-ink-subtle">
                {format === 'gif'
                  ? 'GIF plays as an animated avatar on OpenSea and Discord.'
                  : 'Best quality for posts and timelines.'}
              </p>

              {/* HD — the hero option. */}
              <button
                type="button"
                onClick={() => void run(true)}
                disabled={!capture}
                className={cn(
                  'group relative flex items-center gap-3 overflow-hidden rounded-2xl border p-4 text-left transition-all duration-200',
                  'border-accent/55 bg-gradient-to-br from-accent-glow/12 to-transparent hover:border-accent/80',
                  'shadow-[0_10px_30px_-16px_rgb(var(--color-accent)/0.6)] disabled:opacity-50',
                )}
              >
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-accent/40 bg-ink/20 text-accent-soft">
                  <SparkGlyph />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="flex items-center gap-2">
                    <span className="font-display text-body font-semibold text-ink">HD clip</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-b from-[#f6e3b0] via-[#d7a73e] to-[#a9781f] px-2 py-0.5 font-body text-[0.625rem] font-bold uppercase tracking-wide text-[#3a2a07] shadow-[inset_0_1px_0_rgba(255,255,255,0.45),0_1px_4px_-1px_rgba(0,0,0,0.55)]">
                      <CrownGlyph /> No watermark
                    </span>
                  </span>
                  <span className="font-body text-micro text-ink-soft">
                    {alreadyUnlocked
                      ? 'Already unlocked. Re-download free, video or GIF.'
                      : format === 'gif'
                        ? 'Smooth looping GIF, unmarked.'
                        : 'Crisp 1080p, unmarked. Pure gold, ready to post.'}
                  </span>
                </span>
                <span className="ml-auto flex shrink-0 flex-col items-end gap-0.5">
                  {alreadyUnlocked ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-2.5 py-1 font-body text-micro font-semibold text-accent-deep">
                      Included
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-2.5 py-1 font-body text-micro font-semibold text-accent-deep">
                      <GoldLeafMark className="h-3.5 w-3.5" /> 1 leaf
                    </span>
                  )}
                  <span className="font-body text-[0.625rem] text-ink-subtle transition-transform group-hover:translate-x-0.5">
                    Download →
                  </span>
                </span>
              </button>

              {/* Free — try-it tier. */}
              <button
                type="button"
                onClick={() => void run(false)}
                disabled={!capture}
                className="group flex items-center gap-3 rounded-2xl border border-hairline-strong/55 bg-surface-sunken/40 p-4 text-left transition-colors duration-200 hover:border-hairline-strong hover:bg-surface-sunken/70 disabled:opacity-50"
              >
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-hairline-strong/60 bg-ink/10 text-ink-soft">
                  <FilmGlyph />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="font-display text-body font-semibold text-ink">Free clip</span>
                  <span className="font-body text-micro text-ink-soft">
                    {format === 'gif' ? 'GIF · small gilding.app mark' : '720p · small gilding.app mark'}
                  </span>
                </span>
                <span className="ml-auto font-body text-micro text-ink-subtle transition-transform group-hover:translate-x-0.5">
                  Download →
                </span>
              </button>

              {!capture && (
                <p className="text-center font-body text-micro text-ink-subtle">
                  Preparing the scene… give it a moment.
                </p>
              )}
              {lowLeaves && (
                <p className="text-center font-body text-micro text-danger">
                  Not enough leaves.{' '}
                  <Link to="/buy" className="font-semibold text-accent-deep underline-offset-2 hover:underline">
                    Get more →
                  </Link>
                </p>
              )}
              {error && <p className="text-center font-body text-micro text-danger">{error}</p>}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DonePanel({
  hd,
  saved,
  manualSave,
  previewUrl,
  isGif,
  gifFallback,
  onDownload,
  onShare,
  onAgain,
}: {
  hd: boolean;
  saved: boolean;
  manualSave: boolean;
  previewUrl: string | null;
  isGif: boolean;
  gifFallback: boolean;
  onDownload: () => void;
  onShare: () => void;
  onAgain: () => void;
}) {
  const canShare = canShareClip();
  const touch = isTouchDevice();

  // Video was requested but the browser had no MP4 encoder → it's a GIF. Tell
  // the user plainly so an "image" save isn't a mystery.
  const gifNote = gifFallback ? (
    <p className="rounded-lg border border-hairline-strong/50 bg-surface-sunken/50 px-3 py-2 font-body text-micro text-ink-subtle">
      This browser can’t make an MP4, so we saved a high-quality <strong>GIF</strong> (it saves as
      an image but still animates). For a true video, try Chrome or a newer browser.
    </p>
  ) : null;

  // The browser couldn't save the file directly (iOS in-app browsers open it
  // inline). Show the clip and tell the user exactly how to keep it.
  if (manualSave && previewUrl) {
    return (
      <div className="flex flex-col items-center gap-4 py-1 text-center">
        <div className="w-full max-w-[15rem] overflow-hidden rounded-xl border border-hairline-strong/70 bg-surface-sunken">
          {isGif ? (
            <img src={previewUrl} alt="Your gilding clip" className="block w-full" />
          ) : (
            <video src={previewUrl} controls playsInline loop autoPlay muted className="block w-full" />
          )}
        </div>
        <div className="flex flex-col gap-1">
          <p className="font-display text-h-meta font-semibold text-ink">Your clip is ready</p>
          <p className="font-display text-caption italic text-ink-subtle">
            {isGif
              ? 'Press and hold the image above, then “Save to Photos.”'
              : 'This browser can’t save video directly. Open gilding.app in Safari to download — or export it as a GIF to press-and-hold save.'}
          </p>
        </div>
        <button
          type="button"
          onClick={onAgain}
          className="font-body text-micro text-ink-subtle transition-colors hover:text-ink"
        >
          Make another
        </button>
      </div>
    );
  }

  // Two distinct actions: Download keeps the file; Share posts it. Phones lead
  // with Share (their natural "keep or post" flow); desktop leads with Download.
  const downloadBtn = (
    <button
      type="button"
      onClick={onDownload}
      className={cn('btn-cta w-full', touch && canShare ? 'btn-ghost' : 'btn-primary')}
    >
      <DownloadGlyph />
      {saved ? 'Download again' : 'Download'}
    </button>
  );
  const shareBtn = canShare ? (
    <button
      type="button"
      onClick={onShare}
      className={cn('btn-cta w-full', touch ? 'btn-primary' : 'btn-ghost')}
    >
      <ShareGlyph />
      Share
    </button>
  ) : null;

  return (
    <div className="flex flex-col items-center gap-4 py-1 text-center">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-accent/45 bg-accent-glow/10 text-accent-deep">
        <CheckGlyph />
      </span>
      <div className="flex flex-col gap-1">
        <p className="font-display text-h-meta font-semibold text-ink">
          Your {hd ? 'HD clip' : 'clip'} is ready
        </p>
        <p className="font-display text-caption italic text-ink-subtle">
          {saved ? 'Done — post it and tag us.' : 'Download it to keep it, or share it straight to X.'}
        </p>
      </div>
      {gifNote}
      <div className="flex w-full flex-col gap-2">
        {touch ? (
          <>
            {shareBtn}
            {downloadBtn}
          </>
        ) : (
          <>
            {downloadBtn}
            {shareBtn}
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onAgain}
        className="font-body text-micro text-ink-subtle transition-colors hover:text-ink"
      >
        Make another
      </button>
    </div>
  );
}

function RenderingPanel({ pct, tier }: { pct: number; tier: 'free' | 'hd' }) {
  return (
    <div className="flex flex-col gap-3 py-2">
      <div className="flex items-center justify-between">
        <span className="font-display text-caption italic text-ink-soft">
          {tier === 'hd' ? 'Rendering your HD orbit…' : 'Rendering your clip…'}
        </span>
        <span className="font-body text-caption font-semibold tabular-nums text-accent-deep">{pct}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken">
        <div
          className="h-full rounded-full bg-gradient-to-r from-accent-deep via-accent to-accent-soft transition-[width] duration-150 ease-out"
          style={{ width: `${Math.max(4, pct)}%` }}
        />
      </div>
      <p className="font-body text-micro text-ink-subtle">
        Rendering privately on your device. Nothing leaves your browser.
      </p>
    </div>
  );
}

/** Animate & download — a framed clip (rounded frame + play) with a download arrow
 *  dropping out the bottom. Matches the lightbox CTA icon — shared visual language. */
function AnimateDownloadGlyph({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.75" y="3" width="16.5" height="11.5" rx="2.3" />
      <path d="M10 6.2 L14.4 8.75 L10 11.3 Z" fill="currentColor" stroke="none" />
      <path d="M12 14.6 V21" />
      <path d="M9.3 18.3 L12 21 L14.7 18.3" />
    </svg>
  );
}

function CrownGlyph() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 8l4 3.5L12 4l5 7.5L21 8l-1.8 11H4.8L3 8z" />
    </svg>
  );
}
function CheckGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  );
}
function DownloadGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12M7 10.5l5 5 5-5M5 20.5h14" />
    </svg>
  );
}
function ShareGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 15V3M8.5 6.5L12 3l3.5 3.5" />
      <path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" />
    </svg>
  );
}

/** A clean four-point star (concave sides) with a small companion — the premium mark. */
function SparkGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M11.5 1.8 C12 7.4 15.1 10.5 20.7 11 C15.1 11.5 12 14.6 11.5 20.2 C11 14.6 7.9 11.5 2.3 11 C7.9 10.5 11 7.4 11.5 1.8 Z" />
      <path d="M19.4 14.2 C19.6 16.7 21 18.1 23.5 18.3 C21 18.5 19.6 19.9 19.4 22.4 C19.2 19.9 17.8 18.5 15.3 18.3 C17.8 18.1 19.2 16.7 19.4 14.2 Z" opacity="0.6" />
    </svg>
  );
}

/** A clean video frame with a centred play triangle — the standard clip. */
function FilmGlyph() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="3.4" />
      <path d="M10.2 9.3 L14.9 12 L10.2 14.7 Z" fill="currentColor" stroke="none" />
    </svg>
  );
}
